import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import Loader from '../Loader';
import Alert from '../Alert';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useSheetTouchIsolation from '../../hooks/useSheetTouchIsolation';

const AddCard = ({ show, onClose, onCardAdd, setShowToast }) => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [accounts, setAccounts] = useState(null);
    const [cardType, setCardType] = useState('');
    const [cardNetwork, setCardNetwork] = useState('');
    const [lastFourDigits, setLastFourDigits] = useState('');
    const overlayRef = useRef(null);
    const formRef = useRef(null);

    useBodyScrollLock(show);
    useSheetTouchIsolation(overlayRef, show);

    const accountOptions = accounts?.map((account) => ({
        value: account.id,
        label: account.name,
    }));

    // Handle last 4 digits input - only numeric
    const handleLastFourChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 4);
        setLastFourDigits(value);
    };

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const response = await serverClient.get('/api/user/accounts');
                const fetchedAccounts = response?.data?.accounts;
                setAccounts(fetchedAccounts);

                // Set selectedAccount to the first account in the list
                if (fetchedAccounts && fetchedAccounts.length > 0) {
                    setSelectedAccount({
                        value: fetchedAccounts[0].id,
                        label: fetchedAccounts[0].name,
                    });
                }
            } catch (e) {
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError(t('userNotAuthorized'));
                    navigate('/login');
                } else {
                    setError(t('somethingWentWrong'));
                }
            }
        };

        setLoading(true);
        fetchAccounts();
        setLoading(false);
    }, [t, navigate]);

    const handleCardFormSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        const card_holder_name = document.getElementById('card_holder_name')?.value;
        const bank_name = document.getElementById('bank_name')?.value;
        const expiry = document.getElementById('expiry')?.value;

        // Validate last 4 digits
        if (lastFourDigits.length !== 4) {
            setError(t('enterLast4Digits'));
            setLoading(false);
            return;
        }

        const payload = {
            card_holder_name,
            bank_name,
            account_id: selectedAccount.value,
            card_number: lastFourDigits, // Only last 4 digits for reference
            network_type: cardNetwork || null,
            expiry: expiry || null,
            cvv: null,
            card_type: cardType || null,
        };

        try {
            const response = await serverClient.post('/api/user/card', payload);
            if (response.status === 201 || response.status === 200) {
                setShowToast(true);
                onCardAdd();
                onClose();
            }
        } catch (error) {
            if (error.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else if (error.response?.status === 400) {
                setError(error.response?.data?.message);
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Loader loading={loading} />
            <div
                ref={overlayRef}
                className={`bottom-sheet-overlay ${show ? 'open' : ''}`}
                onClick={onClose}
                role="presentation"
                aria-hidden={!show}
            >
                <div
                    className="auth-form bottom-sheet add-money-modal"
                    onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the sheet
                    tabIndex={-1}
                >
                    <div className="sheet-header p-2">
                        <button
                            className="close-btn dynamic-color-text"
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            &times;
                        </button>
                        <h2 className="dynamic-color-text text-end">{t('addNewCard')}</h2>
                    </div>
                    <div className="scrollable-content">
                        <section className="section-b-space auth-form p-2">
                            <div className="sheet-body">
                                {error && <Alert alert={'danger'}> {error} </Alert>}
                                <form ref={formRef} onSubmit={handleCardFormSubmit}>
                                    <div className="form-group">
                                        <label
                                            htmlFor="card_holder_name"
                                            className="form-label mb-2"
                                        >
                                            {t('cardholderName')}*
                                        </label>
                                        <div className="form-input mb-3">
                                            <input
                                                id="card_holder_name"
                                                type="text"
                                                required
                                                className="form-control"
                                                placeholder={t('cardholderName')}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="bank_name" className="form-label mb-2">
                                            {t('bankName')}*
                                        </label>
                                        <div className="form-input mb-3">
                                            <input
                                                id="bank_name"
                                                type="text"
                                                required
                                                className="form-control"
                                                placeholder={t('bankName')}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="linked_account" className="form-label mb-2">
                                            {t('account')}*
                                        </label>
                                        <div className="d-flex form-input gap-2">
                                            <Select
                                                id="linked_account"
                                                options={accountOptions}
                                                value={selectedAccount}
                                                onChange={setSelectedAccount}
                                                placeholder={t('selectAccount')}
                                                isSearchable
                                                classNamePrefix="react-select"
                                                styles={{
                                                    container: (base) => ({
                                                        ...base,
                                                        width: '100%',
                                                    }),
                                                    control: (base) => ({
                                                        ...base,
                                                        width: '100%',
                                                    }),
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Last 4 Digits - Required Field */}
                                    <div className="form-group">
                                        <label htmlFor="card_number" className="form-label mb-2">
                                            {`${t('cardNumber')} (${t('lastFourDigits')})`}*
                                        </label>
                                        <div className="form-input mb-3">
                                            <input
                                                id="card_number"
                                                required
                                                type="text"
                                                className="form-control"
                                                placeholder="1234"
                                                value={lastFourDigits}
                                                onChange={handleLastFourChange}
                                                maxLength="4"
                                                inputMode="numeric"
                                            />
                                            <small className="text-muted d-block mt-1">
                                                {lastFourDigits.length > 0 ? (
                                                    <span
                                                        style={{
                                                            color: '#10b981',
                                                            fontWeight: '500',
                                                        }}
                                                    >
                                                        ✓ {lastFourDigits.length}/4 digits
                                                    </span>
                                                ) : (
                                                    <span>{t('enterLast4Digits')}</span>
                                                )}
                                            </small>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="card_type" className="form-label mb-2">
                                            {t('cardType')}
                                        </label>
                                        <div className="form-input mb-3">
                                            <select
                                                id="card_type"
                                                className="form-select"
                                                value={cardType}
                                                onChange={(e) => setCardType(e.target.value)}
                                            >
                                                <option value="">{t('selectOption')}</option>
                                                <option value="credit_card">
                                                    {t('creditCard')}
                                                </option>
                                                <option value="debit_card">{t('debitCard')}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="card_network" className="form-label mb-2">
                                            {t('cardNetwork')}
                                        </label>
                                        <div className="form-input mb-3">
                                            <select
                                                id="card_network"
                                                className="form-select"
                                                value={cardNetwork}
                                                onChange={(e) => setCardNetwork(e.target.value)}
                                            >
                                                <option value="">{t('selectOption')}</option>
                                                <option value="amex">{t('americanExpress')}</option>
                                                <option value="visa">{t('visa')}</option>
                                                <option value="master">{t('mastercard')}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="expiry" className="form-label mb-2">
                                            {t('expiry')}
                                        </label>
                                        <div className="form-input mb-3">
                                            <input
                                                id="expiry"
                                                type="date"
                                                className="form-control"
                                                placeholder={t('expiry')}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </section>
                    </div>
                    {/* Fixed Add Transaction button */}
                    <div className="fixed-footer">
                        <button
                            type="submit"
                            onClick={() => formRef.current?.requestSubmit()}
                            className="btn theme-btn successfully w-100"
                        >
                            {t('addNewCard')}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddCard;
